// PromoteVideoUploads.jsx — TRAVEL.mp4 + REVIEWS.mp4 upload slots for the
// Partner / Promote tab. Files persist in IndexedDB across page reloads,
// so the user only uploads once. Replace / Remove always available.

import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, Film, Star, CheckCircle2, X, Replace } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { getFile, saveFile, removeFile } from "../lib/videoStorage";

const ACCEPT = "video/mp4,video/quicktime,video/x-m4v,video/webm";

const SLOT_META = {
  travel: {
    label:    "TRAVEL footage",
    icon:     Film,
    color:    "amber",
  },
  reviews: {
    label:    "REVIEWS footage",
    icon:     Star,
    color:    "emerald",
  },
};

const UploadSlot = ({ slot, onChange }) => {
  const meta = SLOT_META[slot];
  const Icon = meta.icon;
  const [row, setRow] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    getFile(slot).then((r) => { if (!cancel) setRow(r); });
    return () => { cancel = true; };
  }, [slot]);

  const handlePick = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a video file (MP4, MOV, WebM)");
      return;
    }
    setBusy(true);
    try {
      await saveFile(slot, file);
      const r = await getFile(slot);
      setRow(r);
      onChange?.(slot, r);
      toast.success(`${meta.label} saved`);
    } catch (err) {
      toast.error("Couldn't save the file — " + (err?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    await removeFile(slot);
    setRow(null);
    onChange?.(slot, null);
    toast.success(`${meta.label} removed`);
  };

  const colorRing = meta.color === "amber" ? "ring-amber-500/30 bg-amber-500/5" : "ring-emerald-500/30 bg-emerald-500/5";
  const colorIcon = meta.color === "amber" ? "text-amber-300" : "text-emerald-300";

  return (
    <div
      data-testid={`promote-upload-${slot}`}
      className={`relative rounded-2xl border border-white/10 p-4 ring-1 ${colorRing} transition-all`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleFile}
        data-testid={`promote-upload-${slot}-input`}
        className="hidden"
      />

      <div className="flex items-start gap-3">
        <div className={`shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-900 ${colorIcon} ring-1 ring-white/10`}>
          <Icon className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm font-bold tracking-tight text-white">{meta.label}</div>
        </div>
      </div>

      <div className="mt-4">
        {row ? (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-zinc-950/60 border border-emerald-500/20 px-3 py-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" strokeWidth={2.2} />
              <div className="min-w-0">
                <div className="text-[12.5px] text-zinc-200 truncate">{row.name}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={handlePick}
                disabled={busy}
                data-testid={`promote-upload-${slot}-replace`}
                className="h-8 rounded-full border border-white/10 hover:border-amber-400/50 hover:bg-amber-500/10 hover:text-amber-200 text-[11px] px-3"
              >
                <Replace className="h-3.5 w-3.5 mr-1" strokeWidth={2} />
                Replace
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRemove}
                disabled={busy}
                data-testid={`promote-upload-${slot}-remove`}
                className="h-8 w-8 p-0 rounded-full hover:bg-rose-500/10 hover:text-rose-300"
                aria-label="Remove"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            onClick={handlePick}
            disabled={busy}
            data-testid={`promote-upload-${slot}-pick`}
            className={`w-full h-12 rounded-xl border-2 border-dashed ${meta.color === "amber" ? "border-amber-500/30 hover:border-amber-400/60 hover:bg-amber-500/10 text-amber-200" : "border-emerald-500/30 hover:border-emerald-400/60 hover:bg-emerald-500/10 text-emerald-200"} bg-zinc-950/30 text-sm font-semibold transition-all`}
          >
            <UploadCloud className="h-4 w-4 mr-2" strokeWidth={2} />
            {busy ? "Saving…" : `Upload ${meta.label}`}
          </Button>
        )}
      </div>
    </div>
  );
};

export const PromoteVideoUploads = ({ onChange }) => {
  return (
    <div className="rounded-2xl border border-white/10 bg-zinc-950/40 p-4 sm:p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
            Step 1 · Your footage (one-time setup)
          </div>
          <h3 className="font-display text-base sm:text-lg font-black tracking-tight text-white mt-0.5">
            Upload your travel + review videos
          </h3>
          <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5">
            Files stay 100% in your browser — they never upload to a server. Once saved, every future &ldquo;Generate Content&rdquo; reuses them automatically until you replace or remove.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
        <UploadSlot slot="travel" onChange={onChange} />
        <UploadSlot slot="reviews" onChange={onChange} />
      </div>
    </div>
  );
};

export default PromoteVideoUploads;
